import { useEffect, useRef, useState } from 'react'
import { cx } from './core.tsx'

/**
 * Combo de filtro (SPEC-20261003-0927).
 *
 * Um botao que diz QUAL filtro esta valendo e abre a lista dos filtros: cada um
 * com editar e remover ao lado do nome, e "novo" no fim. Nasceu dentro da barra
 * do quadro (SPEC-20261002-2015) e saiu de la para servir a qualquer tela —
 * hoje o quadro e a lista de Itens de Trabalho.
 *
 * Ele NAO sabe o que e um filtro: recebe uma lista de `{ id, name }` e devolve
 * cliques. E o que permite a mesma lista servir a dois modelos de dados
 * diferentes (um no navegador, outro no servidor, com editores proprios) sem
 * precisar fundir os dois.
 */

export type FilterComboOption = {
  id: string
  name: string
  /** Mostra o lapis. Falso nos fixos: ninguem edita "Atrasados". */
  editavel?: boolean
  /** Mostra a lixeira. Falso nos fixos, pelo mesmo motivo. */
  removivel?: boolean
  /** Ícone antes do nome (classe Font Awesome) — o de quem vê, numa visão. */
  icone?: string
  /** Texto miúdo depois do nome: "Pública · de Ana". */
  dica?: string
}

export type FilterComboProps = {
  options: readonly FilterComboOption[]
  /** `null` = nenhum filtro, a opcao que abre a lista com o rotulo de "todos". */
  activeId: string | null
  onActivate: (id: string | null) => void
  onEdit?: (option: FilterComboOption) => void
  onRemove?: (option: FilterComboOption) => void
  onNew?: () => void
  /** Rotulo da opcao "sem filtro" — "Todos", "Todos os itens"… */
  allLabel?: string
  novoLabel?: string
  /** Ícone do botão (padrão: funil). */
  icone?: string
  /** O que cada opção é, para os rótulos de acessibilidade: "filtro", "visão". */
  rotuloDoItem?: string
  disabled?: boolean
  className?: string
}

export function FilterCombo({
  options,
  activeId,
  onActivate,
  onEdit,
  onRemove,
  onNew,
  allLabel = 'Todos',
  novoLabel = 'Novo filtro',
  icone = 'fas fa-filter',
  rotuloDoItem = 'filtro',
  disabled = false,
  className,
}: FilterComboProps) {
  const [aberta, setAberta] = useState(false)
  const comboRef = useRef<HTMLDivElement>(null)

  /**
   * Clique em qualquer lugar fora fecha a lista. Ouvir o documento, em vez de
   * cobrir a tela com um painel invisivel, deixa o clique chegar ao controle
   * que a pessoa mirou — fechar a lista nao custa um segundo clique.
   */
  useEffect(() => {
    if (!aberta) return
    const onPointerDown = (event: PointerEvent) => {
      const alvo = event.target
      if (alvo instanceof Node && comboRef.current?.contains(alvo)) return
      setAberta(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [aberta])

  const ativo = options.find((o) => o.id === activeId) ?? null

  const escolher = (id: string | null) => {
    onActivate(id)
    setAberta(false)
  }

  return (
    <div className={cx('ob-filtercombo', className)} ref={comboRef}>
      <button
        type="button"
        className={cx('ob-filtercombo__botao', aberta && 'ob-filtercombo__botao--on')}
        onClick={() => setAberta((v) => !v)}
        aria-expanded={aberta}
        aria-haspopup="listbox"
        disabled={disabled}
      >
        <i className={icone} aria-hidden />
        <span className="ob-filtercombo__atual">{ativo?.name ?? allLabel}</span>
        <i className="fas fa-chevron-down ob-filtercombo__seta" aria-hidden />
      </button>

      {aberta ? (
        <div className="ob-filtercombo__lista" role="listbox">
          {/* o "sem filtro" e uma opcao como as outras: sair de um filtro nao
              pode ser mais dificil do que entrar nele */}
          <button
            type="button"
            role="option"
            aria-selected={activeId === null}
            className={cx('ob-filtercombo__op', activeId === null && 'ob-filtercombo__op--on')}
            onClick={() => escolher(null)}
          >
            <span>{allLabel}</span>
            {activeId === null ? <i className="fas fa-check" aria-hidden /> : null}
          </button>

          {options.map((option) => (
            <span key={option.id} className="ob-filtercombo__linha">
              <button
                type="button"
                role="option"
                aria-selected={activeId === option.id}
                className={cx(
                  'ob-filtercombo__op',
                  activeId === option.id && 'ob-filtercombo__op--on',
                )}
                onClick={() => escolher(option.id)}
              >
                {option.icone ? <i className={cx(option.icone, 'ob-filtercombo__icone')} aria-hidden /> : null}
                <span className="ob-filtercombo__nome">
                  {option.name}
                  {option.dica ? <small className="ob-filtercombo__dica">{option.dica}</small> : null}
                </span>
                {activeId === option.id ? <i className="fas fa-check" aria-hidden /> : null}
              </button>

              {option.editavel !== false && onEdit ? (
                <button
                  type="button"
                  className="ob-filtercombo__acao"
                  aria-label={`Editar ${rotuloDoItem} ${option.name}`}
                  title="Editar"
                  onClick={() => {
                    setAberta(false)
                    onEdit(option)
                  }}
                >
                  <i className="fas fa-pen" aria-hidden />
                </button>
              ) : null}

              {option.removivel !== false && onRemove ? (
                <button
                  type="button"
                  className="ob-filtercombo__acao ob-filtercombo__acao--sair"
                  aria-label={`Remover ${rotuloDoItem} ${option.name}`}
                  title="Remover"
                  onClick={() => onRemove(option)}
                >
                  <i className="fas fa-trash" aria-hidden />
                </button>
              ) : null}
            </span>
          ))}

          {onNew ? (
            <button
              type="button"
              className="ob-filtercombo__novo"
              onClick={() => {
                setAberta(false)
                onNew()
              }}
            >
              <i className="fas fa-plus" aria-hidden /> {novoLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
